// @flow

import { isPlainObject } from 'lodash';

export const assignForcedObject = (object: mixed, assign: {}): {} => {
  return {
    ...(isPlainObject(object) ? object : {}),
    ...assign,
  };
};

export const setInForcedObject = (
  object: mixed,
  key: string,
  value: mixed
): {} => {
  return assignForcedObject(object, {
    [key]: value,
  });
};

export const getFromForcedObject = (object: mixed, key: string): mixed => {
  if (!isPlainObject(object)) {
    return undefined;
  }
  return object[key];
};

/**
 * This function takes an object and flattens it into a single level object.
 * @param {Object} ob - The object to be flattened
 * @returns {Object} - The flattened object
 * @example
 * flattenObject({ a: 1, b: 2, c: { d: 3, e: 4 } });
 * // returns { a: 1, b: 2, c_d: 3, c_e: 4 }
 */
export const flattenObject = (ob: Object) => {
  const result = {};

  for (const key in ob) {
    // Check if the value is an object but not an array, keep
    // null values as they are
    if (
      ob[key] !== null &&
      typeof ob[key] === 'object' &&
      !Array.isArray(ob[key])
    ) {
      // Recursively call the flattenObject function on the value
      const temp = flattenObject(ob[key]);
      // Loop through each key-value pair in the flattened object
      for (const subKey in temp) {
        // Concatenate the keys and add the value to the result object
        result[key + '_' + subKey] = temp[subKey];
      }
    } else {
      // If the value is not an object or is an array, add it to the result object directly
      result[key] = ob[key];
    }
  }
  return result;
};

export const reduceContactFormRequestInput = (nodes: Array<Object>) => {
  // Given a list of `nodes` from a `context` object, return an object with the following shape:
  // {
  //   category: 'category',
  //   topic: 'topic', // or null
  //   issue: 'issue', // or null
  //   subject: 'subject', // this might be some field from the form
  //   message: 'message', // this might be some other field from the form
  // }

  const initialResult = {
    category: null,
    topic: null,
    issue: null,
  };

  const result = nodes.reduce((acc, node, index) => {
    // ignore the first node
    if (index === 0) {
      return acc;
    }

    // if this is the second node, then it's the category
    if (index === 1) {
      return {
        ...acc,
        category: node.data,
      };
    }

    // if this is the third node and the node.id is not 'help', then it's the topic
    if (index === 2 && node.id !== 'help') {
      return {
        ...acc,
        topic: node.data,
      };
    }

    // if this is the fourth node and the node.id is not 'help', then it's the issue
    if (index === 3 && node.id !== 'help') {
      return {
        ...acc,
        issue: node.data,
      };
    }

    // if this is the last node and the node.id is 'help', then this contains
    // the contact form fields like "subject" and "message"
    if (index === nodes.length - 1 && node.id === 'help') {
      return {
        ...acc,
        ...node.data,
      };
    }
    return acc;
  }, initialResult);

  // This should already be flat, but just in case the data structure changes
  // in the future, we'll future-proof it by flattening it definitively.
  return flattenObject(result);
};
